import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Input,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import Can from '../../../Components/Common/Can';
import ActionButton from '../../../Components/Common/ActionButton';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { crudeApi } from '../../../helpers/crude_api_helper';
import ImportCrudeModal from './components/ImportCrudeModal';

// Backend entity: Crude — Crude Registration list.
// GetListCrude only ever returns Active (!isDeleted) rows — crude submitted
// via Create/AddDetail/UpdateDetail stays invisible here until it clears the
// ChangedRequest Review -> Approve workflow (see api-crude-registration.md).
const LIST_ENDPOINT = '/v1/Crude/GetListCrude';

// Server-side sort whitelist (PascalCase property names — wrong casing -> 422).
const SORT_FIELDS = {
  crudeName: 'CrudeName',
  origin: 'Origin',
  country: 'Country',
  codeG5: 'CodeG5',
};

const RegistrationListView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Server-side pagination / search / sort state.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [importModalOpen, setImportModalOpen] = useState(false);

  document.title = 'Crude Registration';

  // Debounce the search box, then reset to page 1 for a fresh server query.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await crudeApi.get(LIST_ENDPOINT, {
        page,
        pageSize,
        searchText: debouncedSearch || undefined,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Server-side sort: each data column carries a PascalCase `sortField`.
  const handleSort = (column, direction) => {
    if (!column?.sortField) return;
    setSortField(column.sortField);
    setSortOrder(direction === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      name: headerLabel('Crude Name'),
      selector: row => row.crudeName,
      sortable: true,
      sortField: SORT_FIELDS.crudeName,
      wrap: true,
      grow: 2,
    },
    {
      name: headerLabel('Origin'),
      selector: row => row.origin,
      sortable: true,
      sortField: SORT_FIELDS.origin,
      wrap: true,
    },
    {
      name: headerLabel('Country'),
      selector: row => row.country,
      sortable: true,
      sortField: SORT_FIELDS.country,
      wrap: true,
    },
    {
      name: headerLabel('Code G5'),
      selector: row => row.codeG5,
      sortable: true,
      sortField: SORT_FIELDS.codeG5,
      wrap: true,
    },
    {
      name: headerLabel('Action'),
      width: '90px',
      center: true,
      cell: row => (
        <Link
          to={`/crude/registration/dtl/${row.id}`}
          className='btn btn-ghost-secondary btn-sm btn-icon hy-icon-action'
          title='Detail'
          aria-label='Detail'
        >
          <i className='ri-eye-line align-middle'></i>
        </Link>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Registration' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='RegistrationList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Registration
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchKeyword}
                            onChange={e => setSearchKeyword(e.target.value)}
                          />
                        </div>
                        <Can action='canCreate'>
                          <ActionButton
                            variant='secondary'
                            size='sm'
                            icon='ri-file-excel-2-line'
                            onClick={() => setImportModalOpen(true)}
                          >
                            Import Excel
                          </ActionButton>
                        </Can>
                        <Can action='canCreate'>
                          <Link
                            to='/crude/registration/create'
                            className='btn btn-primary btn-label right ms-auto nexttab btn-sm'
                          >
                            <i className='ri-play-list-add-line label-icon align-middle fs-16 ms-2'></i>{' '}
                            <span className='d-none d-sm-inline'>
                              Registration
                            </span>
                            <span className='d-sm-none'>New</span>
                          </Link>
                        </Can>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No crude found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <ImportCrudeModal
            isOpen={importModalOpen}
            toggle={() => setImportModalOpen(false)}
            onImportSuccess={() => {
              setImportModalOpen(false);
              fetchData();
            }}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RegistrationListView;
